import { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Layers, Plus, Loader2, Copy, AlertTriangle, CheckCircle2 } from 'lucide-react';

const brl = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;
const dataBR = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '—');

export default function IntegrationsPlanCard({ api, refreshKey }) {
  const [info, setInfo] = useState(null);
  const [pix, setPix] = useState(null);
  const [buying, setBuying] = useState(false);
  const timer = useRef(null);

  const load = () => api.get('/integrations/billing').then((r) => setInfo(r.data)).catch(() => {});
  useEffect(() => { load(); }, [api, refreshKey]);
  useEffect(() => () => timer.current && clearInterval(timer.current), []);

  const buy = async (slotId = null) => {
    try {
      setBuying(true);
      const r = await api.post('/integrations/slots/purchase', { slot_id: slotId });
      setPix(r.data);
      timer.current && clearInterval(timer.current);
      timer.current = setInterval(async () => {
        try {
          const s = await api.get(`/integrations/slots/payment/${r.data.id}`);
          if (s.data.status === 'approved') {
            clearInterval(timer.current);
            setPix(null);
            toast.success('Pagamento aprovado! Vaga de integração liberada.');
            load();
          }
        } catch { /* segue tentando */ }
      }, 5000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao gerar pagamento');
    } finally {
      setBuying(false);
    }
  };

  const closePix = (open) => {
    if (!open) { setPix(null); timer.current && clearInterval(timer.current); }
  };

  if (!info || info.unlimited) return null;

  const cheio = info.used >= info.limit;
  const qrSrc = pix?.qr_code
    ? (pix.qr_code.startsWith('http') || pix.qr_code.startsWith('data:') ? pix.qr_code : `data:image/png;base64,${pix.qr_code}`)
    : (pix?.qr_code_text ? `https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${encodeURIComponent(pix.qr_code_text)}` : '');

  return (
    <Card data-testid="integrations-plan-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Layers className="w-5 h-5 text-primary" />
            <div>
              <p className="font-medium">
                {info.used} de {info.limit} integrações em uso
                {cheio && <Badge variant="outline" className="ml-2 text-[10px] text-amber-500 border-amber-500/30">Limite atingido</Badge>}
              </p>
              <p className="text-xs text-muted-foreground">
                {info.free} grátis{info.extra_slots.length > 0 && ` + ${info.extra_slots.length} extra(s)`} · cada integração extra custa {brl(info.price)}/mês
              </p>
            </div>
          </div>
          <Button variant={cheio ? 'default' : 'outline'} size="sm" onClick={() => buy()} disabled={buying} data-testid="buy-integration-slot">
            {buying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Adicionar integração extra ({brl(info.price)}/mês)
          </Button>
        </div>

        {info.extra_slots.length > 0 && (
          <div className="space-y-1.5">
            {info.extra_slots.map((s, i) => (
              <div key={s.id} className="flex items-center justify-between text-sm rounded-md border border-border/50 px-3 py-2">
                <span>Integração extra {i + 1} · vence em <b>{dataBR(s.expires_at)}</b></span>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => buy(s.id)} disabled={buying}>
                  Renovar {brl(info.price)}
                </Button>
              </div>
            ))}
          </div>
        )}

        {info.suspended.length > 0 && (
          <div className="rounded-md border border-red-500/30 bg-red-500/5 p-3 text-sm space-y-1">
            <p className="flex items-center gap-2 text-red-400 font-medium"><AlertTriangle className="w-4 h-4" /> Integrações suspensas por falta de pagamento</p>
            {info.suspended.map((s) => (
              <p key={s.id} className="text-xs text-muted-foreground">{s.name || s.display_name || s.panel_username} ({(s.panel_url || '').replace(/^https?:\/\//, '')})</p>
            ))}
            <p className="text-xs text-muted-foreground">Adicione uma integração extra para reativar automaticamente.</p>
          </div>
        )}
      </CardContent>

      <Dialog open={!!pix} onOpenChange={closePix}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Integração extra — {brl(pix?.amount)}</DialogTitle>
            <DialogDescription>Pague o PIX abaixo. A vaga é liberada automaticamente por 30 dias.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {qrSrc && <img src={qrSrc} alt="QR Code PIX" className="w-56 h-56 mx-auto rounded-lg bg-white p-2" />}
            {pix?.qr_code_text && (
              <Button variant="outline" className="w-full" onClick={() => { navigator.clipboard.writeText(pix.qr_code_text); toast.success('Código PIX copiado'); }}>
                <Copy className="w-4 h-4 mr-2" /> Copiar código PIX
              </Button>
            )}
            <p className="text-xs text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Aguardando pagamento…
            </p>
            <p className="text-[11px] text-muted-foreground text-center flex items-center justify-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Pode fechar esta janela: a liberação acontece mesmo assim.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
